<template>
    <div :class="styleClass">
        <template
            v-for="(part, idx) in textParts"
            :key="idx"
        >
            <TextyFigura
                v-if="part.type === 'image' && part.imageKod"
                :item="
                    tema.METADATA_FIGURA?.find(
                        figura =>
                            figura.ID_FIGURA.trim() === part.imageKod?.trim()
                    )
                "
                :is-last-part="idx === textParts.length - 1 && isLastDiv"
            />
            <TextyLinkToImage
                v-else-if="part.type === 'link-image'"
                :html="part.html"
            />
            <TextyLinkToPriloha
                v-else-if="part.type === 'link-priloha'"
                :html="part.html"
            />
            <TextyLinkToTema
                v-else-if="part.type === 'link-tema'"
                :html="part.html"
            />
            <TextyLinkToLimit
                v-else-if="part.type === 'link-limit'"
                :html="part.html"
            />
            <TextyLinkToMetrika
                v-else-if="part.type === 'link-metrika'"
                :html="part.html"
            />
            <TextyLinkToVykres
                v-else-if="part.type === 'link-vykres'"
                :html="part.html"
            />
            <TextyLinkToPortal
                v-else-if="part.type === 'link-portal'"
                :html="part.html"
            />
            <TextyFootnote
                v-else-if="part.type === 'footnote'"
                :text="part.html"
                :tema
            />
            <TextyBibliografie
                v-else-if="part.type === 'biblio'"
                :text="part.html"
                :tema
            />
            <TextyTermit
                v-else-if="part.type === 'termit'"
                :text="part.html"
                :tema
            />
            <ul v-else-if="styleClass === 'uap13'">
                <TextyListParts
                    :tema
                    :html="cleanText(part.html)"
                />
            </ul>
            <ol v-else-if="styleClass === 'uap12'">
                <TextyListParts
                    :tema
                    :html="cleanText(part.html)"
                />
            </ol>
            <div
                v-else
                :class="{
                    inline: styleClass !== 'uap13' && styleClass !== 'uap12',
                    contents: styleClass === 'uap13' || styleClass === 'uap12',
                }"
                v-html="correctTypography(cleanText(part.html))"
            />
        </template>
    </div>
</template>

<script lang="ts" setup>
import correctTypography from "~/utils/correctTypography"

const props = defineProps<{
    html: string
    styleClass: string
    tema: TemaItem
    isLastDiv: boolean
}>()

const REGEX_STYLES =
    '((<span class="uap[z]?\\d{1,2}(-odkaz)?(c)?">).*?(</span>))'
const REGEX_STYLE_CODE = "uap[z]?\\d{1,2}(-odkaz)?(c)?"
// const REGEX_FOOTNOTE = '(<sup><a id="truefootnote-ref-\\d+">\\[\\d+\\]</a></sup>)'
const REGEX_FOOTNOTE =
    '(<sup><a (target="_blank" )?((href="#truefootnote-\\d+")|(id="truefootnote-ref-\\d+")) ((href="#truefootnote-\\d+")|(id="truefootnote-ref-\\d+"))>\\[\\d+\\]<\\/a><\\/sup>)'
const REGEX_BIBLIO = "\\(\\d{1,2}\\)|\\(\\d{1,2} str\\. \\d{1,2}\\)"
const REGEX_TERMIT = '<span[^>]*resource="[^"]+"[^>]*>.*?</span>'
const REGEX_LINK_PORTAL = '<a[^>]*>(<span class="uapz11">.*?<\\/span>)<\\/a>'

type TextPartType =
    | "text"
    | "image"
    | "footnote"
    | "link-image"
    | "link-priloha"
    | "link-tema"
    | "link-jev"
    | "link-limit"
    | "link-metrika"
    | "link-vykres"
    | "link-portal"
    | "biblio"
    | "termit"
    | "unordered-list"
    | "ordered-list"
type TextPart = {
    html: string
    type: TextPartType
    imageKod?: string
}

function cleanText(text: string) {
    return text
        .replaceAll("", "<i class='pi pi-arrow-right text-sm'></i>")
        .replaceAll("🡪", "<i class='pi pi-arrow-right text-sm'></i>")
        .replaceAll("🡨", "<i class='pi pi-arrow-left text-sm'></i>")
}

const textParts = computed<TextPart[]>(() => {
    if (props.styleClass === "uap13" || props.styleClass === "uap12") {
        return [
            {
                html: props.html,
                type:
                    props.styleClass === "uap13"
                        ? "unordered-list"
                        : "ordered-list",
            },
        ]
    }
    const parts = splitTextToParts(props.html)
    return processParts(parts)
})

function splitTextToParts(text: string) {
    const regex = new RegExp(
        `${REGEX_FOOTNOTE}|${REGEX_STYLES}|${REGEX_BIBLIO}|${REGEX_TERMIT}|${REGEX_LINK_PORTAL}`,
        "g"
    )
    const matches = text.match(regex)

    // Split the text manually
    const parts: string[] = []
    let lastIndex = 0

    if (matches) {
        matches.forEach(match => {
            const index = text.indexOf(match, lastIndex)
            if (index > lastIndex) {
                parts.push(text.substring(lastIndex, index))
            }
            parts.push(match)
            lastIndex = index + match.length
        })
    }

    // Add any remaining text after the last match
    if (lastIndex < text.length) {
        parts.push(text.substring(lastIndex))
    }
    return parts
}

function processParts(parts: string[]): Array<TextPart> {
    return parts.map(part => {
        if (props.styleClass === "uap13") {
            return {
                html: part,
                type: "unordered-list",
            }
        }
        if (props.styleClass === "uap12") {
            return {
                html: part,
                type: "ordered-list",
            }
        }

        if (part.match(new RegExp(REGEX_LINK_PORTAL))) {
            return {
                html: part,
                type: "link-portal",
            }
        }

        if (part.match(new RegExp(REGEX_STYLES))) {
            const style = part.match(new RegExp(REGEX_STYLE_CODE))?.[0]
            if (style === "uap15") {
                const kod = part.match(/uap15">(.*)<\/span>/)?.[1]
                return {
                    html: part,
                    type: "image",
                    imageKod: kod
                        ?.replace(/\s/g, "")
                        .replace("50.", "050.")
                        .slice(1, -1),
                }
            }

            if (style === "uapz09" && part.includes("P.")) {
                return {
                    html: part,
                    type: "link-priloha",
                }
            }
            if (style === "uapz09") {
                return {
                    html: part,
                    type: "link-image",
                }
            }
            if (style === "uapz08" && part.match(new RegExp(REGEX_BIBLIO))) {
                return {
                    html: part,
                    type: "biblio",
                }
            }
            if (style === "uap02-odkaz" || style === "uapz02") {
                return {
                    html: part,
                    type: "link-tema",
                }
            }
            if (style === "uapz05") {
                return {
                    html: part,
                    type: "link-limit",
                }
            }
            if (style === "uapz06") {
                return {
                    html: part,
                    type: "link-metrika",
                }
            }
            if (style === "uapz10") {
                return {
                    html: part,
                    type: "link-vykres",
                }
            }
            return {
                html: cleanText(part),
                type: "text",
            }
        }
        if (part.match(new RegExp(REGEX_FOOTNOTE))) {
            return {
                html: part,
                type: "footnote",
            }
        }
        if (part.match(new RegExp(REGEX_BIBLIO))) {
            return {
                html: (
                    part.match(new RegExp(REGEX_BIBLIO)) as RegExpMatchArray
                )[0],
                type: "biblio",
            }
        }
        if (part.match(new RegExp(REGEX_TERMIT))) {
            return {
                html: part,
                type: "termit",
            }
        }
        return {
            html: part,
            type: "text",
        }
    })
}
</script>

<style scoped></style>
